import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../context/AppContext';

const UserManagement = () => {
    const { t } = useTranslation();
    const { token, depos, authFetch } = useContext(AppContext);

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'

    const initialFormState = {
        id: null,
        name: '',
        email: '',
        username: '',
        password: '',
        role: 'Chef Depo',
        depo_id: '',
        allowed_depos: []
    };

    const [formData, setFormData] = useState(initialFormState);

    const roles = ['Admin', 'Manager', 'Controller', 'Cashier', 'Chef Depo'];

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const res = await authFetch('/api/users');
            if (!res.ok) throw new Error('Failed to fetch users');
            const data = await res.json();
            setUsers(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (mode, user = null) => {
        setModalMode(mode);
        if (mode === 'edit' && user) {
            setFormData({
                id: user.id,
                name: user.name || '',
                email: user.email || '',
                username: user.username || '',
                password: '', // blank for edit
                role: user.role,
                depo_id: user.depo_id || '',
                allowed_depos: user.allowed_depos ? JSON.parse(user.allowed_depos) : []
            });
        } else {
            setFormData(initialFormState);
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setFormData(initialFormState);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleAllowedDeposChange = (e) => {
        const options = e.target.options;
        const selected = [];
        for (let i = 0; i < options.length; i++) {
            if (options[i].selected) {
                selected.push(parseInt(options[i].value));
            }
        }
        setFormData({ ...formData, allowed_depos: selected });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = modalMode === 'add'
                ? '/api/users'
                : `/api/users?id=${formData.id}`;
            const method = modalMode === 'add' ? 'POST' : 'PUT';

            const payload = { ...formData };
            if (modalMode === 'edit' && !payload.password) {
                delete payload.password;
            }

            const res = await authFetch(url, {
                method,
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.message || 'Action failed');
            }

            fetchUsers();
            handleCloseModal();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('Are you sure you want to delete this user?'))) return;
        try {
            const res = await authFetch(`/api/users?id=${id}`, {
                method: 'DELETE'
            });
            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.message || 'Delete failed');
            }
            fetchUsers();
        } catch (err) {
            alert(err.message);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-slate-800">{t('User Management')}</h1>
                <button
                    onClick={() => handleOpenModal('add')}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition"
                >
                    + {t('Add User')}
                </button>
            </div>

            {error && <div className="bg-red-100 text-red-700 p-4 rounded-lg">{error}</div>}

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="w-full overflow-x-auto max-w-full">
<table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th className="px-6 py-4 font-semibold text-slate-800">{t('Name')}</th>
                            <th className="px-6 py-4 font-semibold text-slate-800">{t('Username')}</th>
                            <th className="px-6 py-4 font-semibold text-slate-800">{t('Role')}</th>
                            <th className="px-6 py-4 font-semibold text-slate-800">{t('Primary Depo')}</th>
                            <th className="px-6 py-4 font-semibold text-slate-800 text-right">{t('Actions')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr><td colSpan="5" className="text-center py-4">{t('Loading...')}</td></tr>
                        ) : users.length === 0 ? (
                            <tr><td colSpan="5" className="text-center py-4 text-slate-500">{t('No users found')}</td></tr>
                        ) : (
                            users.map(u => {
                                const userDepo = depos.find(d => d.id == u.depo_id);
                                return (
                                    <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-4 font-medium text-slate-800">
                                            {u.name || '-'}
                                            <div className="text-xs text-slate-500">{u.email}</div>
                                        </td>
                                        <td className="px-6 py-4">{u.username}</td>
                                        <td className="px-6 py-4">
                                            <span className="bg-slate-100 text-slate-700 px-2 py-1 rounded text-xs font-semibold">
                                                {u.role}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">{userDepo ? userDepo.name : '-'}</td>
                                        <td className="px-6 py-4 text-right space-x-2">
                                            <button
                                                onClick={() => handleOpenModal('edit', u)}
                                                className="text-indigo-600 hover:text-indigo-800"
                                            >
                                                {t('Edit')}
                                            </button>
                                            <button
                                                onClick={() => handleDelete(u.id)}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                {t('Delete')}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
</div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
                        <div className="p-6 border-b border-slate-200 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-slate-800">
                                {modalMode === 'add' ? t('Add New User') : t('Edit User')}
                            </h2>
                            <button onClick={handleCloseModal} className="text-slate-400 hover:text-slate-600">✕</button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('Name')}</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('Email')}</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('Username')} *</label>
                                    <input
                                        type="text"
                                        name="username"
                                        value={formData.username}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                        {t('Password')} {modalMode === 'edit' && <span className="text-xs text-slate-400">(Leave blank to keep current)</span>}
                                    </label>
                                    <input
                                        type="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        required={modalMode === 'add'}
                                        className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">{t('Role')} *</label>
                                <select
                                    name="role"
                                    value={formData.role}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                                >
                                    {roles.map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">{t('Primary Depo/Store')}</label>
                                <select
                                    name="depo_id"
                                    value={formData.depo_id}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                                >
                                    <option value="">{t('None (Global Access)')}</option>
                                    {depos.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">{t('Allowed Depos (Ctrl+Click)')}</label>
                                <select
                                    multiple
                                    name="allowed_depos"
                                    value={formData.allowed_depos}
                                    onChange={handleAllowedDeposChange}
                                    className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none h-24"
                                >
                                    {depos.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                                <p className="text-xs text-slate-500 mt-1">If empty, follows Role default access.</p>
                            </div>

                            <div className="pt-4 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="px-4 py-2 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition"
                                >
                                    {t('Cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition"
                                >
                                    {modalMode === 'add' ? t('Add User') : t('Save Changes')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
